// Thin REST client for the Worker API. Every body is already ciphertext by the time
// it reaches here; this layer only adds the bearer token and parses responses.
import type {
  RegisterReq,
  PrekeyBundle,
  PublicIdentity,
  SendReq,
  SendRes,
  Tombstone,
  PurgeDirective,
  MessageEnvelope,
  KeyDistribution,
} from "@shared/wire.js";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export class Api {
  constructor(private token: string | null = null) {}

  setToken(t: string | null): void {
    this.token = t;
  }
  getToken(): string | null {
    return this.token;
  }

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    let payload: BodyInit | undefined;
    if (body instanceof Uint8Array) payload = body as BodyInit;
    else if (body !== undefined) {
      headers["content-type"] = "application/json";
      payload = JSON.stringify(body);
    }
    const res = await fetch(`/api${path}`, { method, headers, body: payload });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
      throw new ApiError(res.status, e.error ?? "request failed");
    }
    return (res.headers.get("content-type") ?? "").includes("json") ? ((await res.json()) as T) : (res as unknown as T);
  }

  register(req: RegisterReq) {
    return this.req<{ user_id: string; token: string }>("POST", "/register", req);
  }
  loginStart(handle: string) {
    return this.req<{ salt: string; challenge: string; challenge_id: string }>("POST", "/login/start", { handle });
  }
  loginFinish(challenge_id: string, signature: string) {
    return this.req<{ user_id: string; token: string; key_bundle: string }>("POST", "/login/finish", { challenge_id, signature });
  }
  me() {
    return this.req<{ user_id: string; handle: string; key_bundle: string }>("GET", "/me");
  }
  lookup(handle: string) {
    return this.req<PublicIdentity>("GET", `/users/lookup?handle=${encodeURIComponent(handle)}`);
  }
  prekeys(userId: string) {
    return this.req<PrekeyBundle>("GET", `/prekeys/${userId}`);
  }
  replenishPrekeys(opks: { opk_id: number; opk_pub: string }[]) {
    return this.req<{ available: number }>("POST", "/prekeys", { one_time_prekeys: opks });
  }
  createConv(type: "dm" | "group", members: string[]) {
    return this.req<{ conv_id: string }>("POST", "/conv", { type, members });
  }
  listConv() {
    return this.req<{ conversations: ServerConv[] }>("GET", "/conv");
  }
  getConv(id: string) {
    return this.req<ServerConv>("GET", `/conv/${id}`);
  }
  send(convId: string, body: SendReq) {
    return this.req<SendRes>("POST", `/conv/${convId}/send`, body);
  }
  backlog(convId: string, sinceSeq: number, sinceTomb: number) {
    return this.req<{ messages: MessageEnvelope[]; tombstones: { msg_id: string }[] }>(
      "GET",
      `/conv/${convId}/messages?since_seq=${sinceSeq}&since_tomb=${sinceTomb}`,
    );
  }
  tombstone(convId: string, tomb: Tombstone) {
    return this.req<{ ok: true }>("POST", `/conv/${convId}/tombstone`, tomb);
  }
  purge(convId: string, dir: PurgeDirective) {
    return this.req<{ ok: true; purged: number }>("POST", `/conv/${convId}/purge`, dir);
  }
  addMember(convId: string, user_id: string) {
    return this.req<{ ok: true }>("POST", `/conv/${convId}/members`, { user_id });
  }
  removeMember(convId: string, userId: string) {
    return this.req<{ ok: true }>("DELETE", `/conv/${convId}/members/${userId}`);
  }
  setTtl(convId: string, ttl_sec: number) {
    return this.req<{ ok: true }>("POST", `/conv/${convId}/ttl`, { ttl_sec });
  }
  postKeydist(convId: string, to_user: string, epoch: number, ciphertext: string) {
    return this.req<{ ok: true }>("POST", `/conv/${convId}/keydist`, { to_user, epoch, ciphertext });
  }
  getKeydist(convId: string, since: number) {
    return this.req<{ distributions: (KeyDistribution & { id: number })[]; members: string[] }>(
      "GET",
      `/conv/${convId}/keydist?since=${since}`,
    );
  }
  uploadAttachment(convId: string, bytes: Uint8Array) {
    return this.req<{ blob_key: string }>("PUT", `/conv/${convId}/attachment`, bytes);
  }
  async downloadAttachment(convId: string, key: string): Promise<Uint8Array> {
    const res = await this.req<Response>("GET", `/conv/${convId}/attachment?key=${encodeURIComponent(key)}`);
    return new Uint8Array(await res.arrayBuffer());
  }
}

export interface ServerConv {
  conv_id: string;
  type: "dm" | "group";
  disappearing_ttl: number;
  members: { user_id: string; handle: string; ik_dh_pub: string; ik_sig_pub: string; role: string }[];
}
